"use client";
import React, { useState, useRef, useEffect } from "react";
import styles from "./MobileChatInput.module.scss";
import Locale from "@/app/locales";
import { webSocketService } from "@/app/services/WebSocketService";
import Image from "next/image";
import EmojiPanel from "./EmojiPanel";
import { useCurrentUser } from "@/app/lib/AuthProvider";
import { getPublicImagePath } from "@/app/utils/image";
import UploadFile from "./UploadFile";
import { getDeviceUa, IsiOS } from "@/app/utils/device";
import ChatInputQuoted from "../ChatInputQuoted";
import MobileFooterTabBar from "../../robot/components/mobile/chatTemplate/footerTabBar/MobileFooterTabBar";
import AutoComplete from "@/app/components/AutoComplete";
import { isHandyApp } from "@/app/utils/device";
import { useMessageStore } from "@/app/store";

interface MobileChatInputProps {
  onSend?: (message: string) => void;
  placeholder?: string;
  onchangeInput: (value: string) => void;
  pageVisible?: boolean;
  disconnected: boolean;
  isSending: boolean;
  stopStreaming: () => void;
  reEditContent?: string | null;
  setReEditContent?: (content: string | null) => void;
}

export default function MobileChatInput({
  onSend,
  placeholder = Locale.chatInput.inputPlaceholder,
  disconnected,
  onchangeInput,
  pageVisible,
  isSending,
  stopStreaming,
  reEditContent,
  setReEditContent,
}: MobileChatInputProps) {
  const [inputVal, setInputVal] = useState<string>("");
  const [showEmojiPanel, setShowEmojiPanel] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isComposing = useRef(false);
  const user = useCurrentUser();
  const regex = /(<([^>]+)>)/gi;

  // 获取全局状态
  const { aiTypingEnd } = useMessageStore();

  React.useEffect(() => {
    console.log("useCurrentUser:", user);
  }, [user]);

  // TODO 合并与PC的发送消息，依赖同一个方法
  const sendMsg = () => {
    if (inputVal.trim()) {
      if (onSend) {
        // 调用父组件的 sendMsg，父组件会处理 SSE 发送和首次自动回复逻辑
        onSend(inputVal);
      } else {
        webSocketService.sendMessage(JSON.stringify({ text: `${inputVal}` }));
      }
      setInputVal("");
      // 发送后关闭所有弹窗
      setShowEmojiPanel(false);
    }
  };
  const handleRealChange = (value: string) => {
    onchangeInput(value);
  };

  const handleInputChange = (value: string) => {
    if (!isComposing.current) {
      handleRealChange(value);
    }
    setInputVal(value);
  };
  const handleAutoSelect = (id: string, option: any) => {
    const label = option?.topic || option?.label || id;
    const content = label.replace(regex, "");

    // 发送消息（通过 onSend 或 websocket）
    // 父组件的 sendMsg 会处理 SSE 发送和首次自动回复逻辑
    if (onSend) {
      onSend(content);
    } else {
      webSocketService.sendMessage(JSON.stringify({ text: `${content}` }));
    }

    setInputVal("");
    setShowEmojiPanel(false);
  };

  // const handleInputChange = (value: string) => {
  //   setInputVal(value);
  // };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMsg();
    }
  };

  const handleInputFocus = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (IsiOS()) {
      if (!/OS 11_[0-3]\D/.test(getDeviceUa())) {
        document.body.scrollTop = document.body.scrollHeight;
      }
    } else {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.scrollIntoView(false);
        }
      }, 200);
    }
  };

  const handleEmojiSelect = (emoji: string) => {
    const input = inputRef.current;
    if (input) {
      const cursorPosition = input.selectionStart || 0;
      const newValue =
        inputVal.slice(0, cursorPosition) +
        emoji +
        inputVal.slice(cursorPosition);

      setInputVal(newValue);

      // 设置光标位置到表情后面
      setTimeout(() => {
        input.focus();
        input.setSelectionRange(
          cursorPosition + emoji.length,
          cursorPosition + emoji.length,
        );
      }, 0);
    }
    setShowEmojiPanel(false);
  };

  const toggleEmojiPanel = () => {
    if (disconnected) {
      return;
    }
    setShowEmojiPanel(!showEmojiPanel);
  };

  const handleCompositionStart = (
    e: React.CompositionEvent<HTMLInputElement>,
  ) => {
    isComposing.current = true;
  };

  // const handleCompositionEnd = (
  //   e: React.CompositionEvent<HTMLInputElement>,
  // ) => {
  //   isComposing.current = false;
  //   handleRealChange(e);
  // };

  useEffect(() => {
    if (!pageVisible && isHandyApp()) {
      inputRef.current?.blur();
    }
  }, [pageVisible]);

  // 撤回后点击「重新编辑」：填入内容并滚动到输入框
  useEffect(() => {
    if (reEditContent != null && setReEditContent) {
      setInputVal(reEditContent);
      setReEditContent(null);
      inputRef.current?.focus();
      requestAnimationFrame(() => {
        containerRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      });
    }
  }, [reEditContent, setReEditContent]);

  // 如果是在 handy 中，并且 URL 中有 question 参数，则填入输入框
  useEffect(() => {
    if (isHandyApp()) {
      const urlParams = new URLSearchParams(window.location.search);
      const question = urlParams.get("question");
      if (question) {
        setInputVal(question);
        onchangeInput(question);
      }
    }
  }, []);

  return (
    <div
      className={`${styles.mobileInputContainer} mobile-chat-input-container`}
      ref={containerRef}
    >
      {/* <EndChatMask isMobile={true} /> */}
      <ChatInputQuoted isMobile={true} />
      <MobileFooterTabBar techAIFlag={isHandyApp()} />
      {/* 主输入区域 */}
      <div className={styles.inputWrapper}>
        {/* 左侧输入框 */}
        <div className={styles.inputField}>
          <AutoComplete
            inputType="input"
            inputRef={inputRef}
            value={inputVal}
            onChange={handleInputChange}
            onKeyDown={handleKeyPress}
            onSelect={handleAutoSelect}
            onFocus={handleInputFocus}
            placeholder={placeholder}
            className={styles.textInput}
            classNames={{ popup: { root: "mobile-autocomplete-popup" } }}
            disabled={isSending || !aiTypingEnd}
          />
        </div>

        {/* 右侧按钮组 */}
        <div className={styles.actionButtons}>
          {!disconnected && (
            <button
              className={`${styles.actionBtn} ${showEmojiPanel ? styles.active : ""}`}
              onClick={toggleEmojiPanel}
            >
              <Image
                src={getPublicImagePath("emojis.png")}
                alt="emoji"
                width={20}
                height={20}
                unoptimized
              />
            </button>
          )}

          {/* 始终保持挂载，避免文件选择器打开期间页面冻结导致组件卸载、file input 丢失 */}
          <div style={disconnected ? { display: "none" } : undefined}>
            <UploadFile disabled={disconnected} />
          </div>

          {/* 停止按钮 - SSE 进行中或打字机效果未结束时显示 */}
          {isSending || !aiTypingEnd ? (
            <div className={styles.stopButtonWrapper} onClick={stopStreaming}>
              <Image
                src={getPublicImagePath("stop.svg")}
                alt="stop"
                width={24}
                height={24}
                unoptimized
                className={styles.stopButton}
              />
            </div>
          ) : (
            /* 发送按钮 - 只在有内容时显示 */
            inputVal.trim() && (
              <Image
                src={getPublicImagePath("send_new.svg")}
                alt="send"
                width={24}
                height={24}
                onClick={sendMsg}
                unoptimized
                className={styles.sendButton}
              />
            )
          )}
        </div>
      </div>

      {/* 表情面板 */}
      <EmojiPanel
        isVisible={showEmojiPanel}
        onEmojiSelect={handleEmojiSelect}
        onClose={() => setShowEmojiPanel(false)}
      />
    </div>
  );
}
